import {
  Box,
  Button,
  CheckIcon,
  Flex,
  Image,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import type { Nullish, PriceId } from '@iheartjane/types';
import { useState } from 'react';

import styles from './addToBagModal.module.css';

export interface WeightOption {
  isDiscounted?: boolean;
  label: string;
  originalPrice?: Nullish<string>;
  potencyLabel?: Nullish<string>;
  price: string;
  value: PriceId;
}

export interface AddToBagModalProps {
  brand?: Nullish<string>;
  disabledWeights?: Set<PriceId>;
  dosageLabel?: Nullish<string>;
  imageUrl?: string;
  isAddingToCart?: boolean;
  isLayered?: boolean;
  onAddToBag: (weight: PriceId) => void;
  onRequestClose: () => void;
  onWeightChange?: (weight: PriceId) => void;
  open: boolean;
  potencyLabel?: Nullish<string>;
  productName: string;
  weights: WeightOption[];
}

export function AddToBagModal({
  brand,
  disabledWeights = new Set<PriceId>(),
  dosageLabel,
  imageUrl,
  isAddingToCart = false,
  isLayered = false,
  onAddToBag,
  onRequestClose,
  onWeightChange,
  open,
  potencyLabel,
  productName,
  weights,
}: AddToBagModalProps) {
  const [selectedWeight, setSelectedWeight] = useState<PriceId | undefined>(
    undefined
  );

  // Clear the selection when the modal closes
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setSelectedWeight(undefined);
  }

  const selectedAtMax = selectedWeight
    ? disabledWeights.has(selectedWeight)
    : false;

  const selectedWeightOption = weights.find(
    ({ value }) => value === selectedWeight
  );
  const displayedPotency = selectedWeightOption?.potencyLabel ?? potencyLabel;

  return (
    <Modal
      contentLabel="Add to bag"
      onRequestClose={onRequestClose}
      open={open}
      overlayClose
      variant="dialogue"
      isLayered={isLayered}
      overlayClassName={isLayered ? styles['layeredOverlay'] : undefined}
    >
      <Modal.Header title="Add to bag" textAlign="center" branded={false} />
      <Modal.Content>
        <Flex flexDirection="column" gap={24}>
          <Box className={styles['productInfo']}>
            <Flex alignItems="center" gap={24}>
              <Image
                altText=""
                borderRadius="rounded-small"
                src={imageUrl ?? ''}
                height="96px"
                width="96px"
              />
              <Flex flexDirection="column" gap={2}>
                <Typography color="text-main" variant="body-bold">
                  {productName}
                </Typography>
                {brand && (
                  <Typography color="text-light" variant="body">
                    {brand}
                  </Typography>
                )}
                {displayedPotency && (
                  <Typography color="text-light" maxLines={1} variant="body">
                    {displayedPotency}
                  </Typography>
                )}
                {dosageLabel && (
                  <Typography color="text-light" maxLines={1} variant="body">
                    {dosageLabel}
                  </Typography>
                )}
              </Flex>
            </Flex>
          </Box>

          <Flex flexDirection="column" gap={16} mt={16}>
            <Typography color="text-main" variant="body-bold">
              Select weight
            </Typography>
            <Flex flexWrap="wrap" gap={16}>
              {weights.map((weight) => (
                <Button
                  key={weight.value}
                  className={styles['weightButton']}
                  disabled={disabledWeights.has(weight.value)}
                  full
                  onClick={() => {
                    setSelectedWeight(weight.value);
                    onWeightChange?.(weight.value);
                  }}
                  size="jumbo"
                  subLabel={weight.label}
                  variant={
                    selectedWeight === weight.value
                      ? 'tertiary-selected'
                      : 'tertiary'
                  }
                  label={
                    <Flex alignItems="center" gap={4}>
                      <Typography
                        variant="body-bold"
                        color={weight.isDiscounted ? 'info' : 'inherit'}
                      >
                        {weight.price}
                      </Typography>
                      {weight.originalPrice && (
                        <Typography
                          color="grays-mid"
                          variant="body-bold"
                          strikeThrough
                        >
                          {weight.originalPrice}
                        </Typography>
                      )}
                    </Flex>
                  }
                />
              ))}
            </Flex>
          </Flex>

          <Flex flexDirection="column" gap={12}>
            <Button
              disabled={!selectedWeight || selectedAtMax || isAddingToCart}
              full
              startIcon={
                isAddingToCart && (
                  <CheckIcon className={styles['textContrastColor']} />
                )
              }
              label={selectedAtMax ? 'Limit reached' : 'Add to bag'}
              onClick={() => selectedWeight && onAddToBag(selectedWeight)}
              variant="primary"
            />
            {/* Mobile-only */}
            <Button
              className={styles['cancelButton']}
              full
              label="Cancel"
              onClick={onRequestClose}
              variant="secondary"
            />
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
}
